import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/store";
import { api } from "@/lib/api";
import { Minus, Plus, Trash } from "@phosphor-icons/react";
import { TID } from "@/lib/testIds";
import { iconFor, tintFor } from "@/lib/categoryIcons";

export default function Cart() {
  const navigate = useNavigate();
  const { items, storeId, inc, dec, remove, subtotal } = useCart();
  const [store, setStore] = useState(null);

  useEffect(() => {
    if (storeId) api.get(`/stores/${storeId}`).then((r) => setStore(r.data.store));
    else api.get("/stores").then((r) => setStore(r.data[0] || null));
  }, [storeId]);

  const sub = subtotal();
  const fee = store?.delivery_fee || 0;
  const tax = +(sub * 0.15).toFixed(2);
  const total = +(sub + fee + tax).toFixed(2);
  const canCheckout = items.length > 0 && store && sub >= store.minimum_order;

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <span className="label-eyebrow">Your basket</span>
        <h1 className="mt-1 font-display text-5xl">Checkout</h1>
        {store && (
          <p className="mt-2 text-sm text-[var(--text-2)]">
            From <span className="font-bold">{store.name}</span> · Min order R{store.minimum_order.toFixed(2)}
          </p>
        )}
      </div>

      {items.length === 0 ? (
        <div className="card-panel p-10 text-center">
          <p className="font-display text-2xl">Your basket is empty.</p>
          <button className="btn-primary mt-6" onClick={() => navigate("/")}>Start shopping</button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {items.map((it) => {
              const { icon: Icon, colour } = iconFor(it.category);
              return (
              <div key={it.product_id} className="card-flat p-4 flex items-center gap-4">
                <div
                  className="w-16 h-16 rounded-xl flex-shrink-0 border-2 border-[var(--text)] flex items-center justify-center"
                  style={tintFor(it.category)}
                >
                  <Icon size={32} weight="regular" color={colour} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black truncate" style={{ fontFamily: "'Fraunces', serif" }}>{it.name}</p>
                  <p className="text-sm text-[var(--text-2)]">R{it.price.toFixed(2)} each</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={TID.decQty}
                    onClick={() => dec(it.product_id)}
                    className="w-8 h-8 rounded-full border-2 border-[var(--text)] bg-white hover:bg-[var(--yellow)] flex items-center justify-center"
                  >
                    <Minus size={12} weight="bold" />
                  </button>
                  <span className="w-6 text-center font-black">{it.quantity}</span>
                  <button
                    data-testid={TID.incQty}
                    onClick={() => inc(it.product_id)}
                    className="w-8 h-8 rounded-full border-2 border-[var(--text)] bg-white hover:bg-[var(--yellow)] flex items-center justify-center"
                  >
                    <Plus size={12} weight="bold" />
                  </button>
                </div>
                <span className="w-24 text-right font-display text-xl">R{(it.price * it.quantity).toFixed(2)}</span>
                <button onClick={() => remove(it.product_id)} className="text-[var(--text-3)] hover:text-[var(--red)]">
                  <Trash size={16} weight="fill" />
                </button>
              </div>
              );
            })}
          </div>

          <div className="card-panel p-6">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--text-2)]">Subtotal</span>
              <span>R{sub.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-[var(--text-2)]">Delivery</span>
              <span>R{fee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-[var(--text-2)]">VAT (15%)</span>
              <span>R{tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-display text-3xl mt-4 pt-4 border-t-2 border-[var(--text)]">
              <span>Total</span>
              <span>R{total.toFixed(2)}</span>
            </div>
            {store && sub < store.minimum_order && (
              <p className="mt-3 text-xs font-bold text-[var(--red)] uppercase tracking-widest">
                Add R{(store.minimum_order - sub).toFixed(2)} more to reach the minimum
              </p>
            )}
            <button
              data-testid={TID.cartCheckoutBtn}
              disabled={!canCheckout}
              onClick={() => navigate("/checkout")}
              className="btn-primary w-full mt-6"
            >
              Continue to payment
            </button>
          </div>
        </>
      )}
    </div>
  );
}
